import { apiUrl } from "./constants";
import type { UserPreferences } from "./preferences";
import type { AgentInstallationStatus } from "@chatcode/protocol";
import type { Locale } from "@chatcode/i18n";
import { webText as t } from "./i18n";

type AuthExpiredListener = () => void;

interface RequestOptions extends RequestInit {
  suppressAuthExpired?: boolean;
}

const authExpiredListeners = new Set<AuthExpiredListener>();
let authProbeInFlight: Promise<boolean> | null = null;

async function parseResponseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205) {
    return undefined;
  }
  const text = await res.text();
  if (!text) {
    return undefined;
  }
  const contentType = res.headers.get("Content-Type") || "";
  if (contentType.includes("application/json")) {
    return JSON.parse(text) as unknown;
  }
  return text;
}

function notifyAuthExpired() {
  for (const listener of authExpiredListeners) {
    listener();
  }
}

export function subscribeAuthExpired(listener: AuthExpiredListener) {
  authExpiredListeners.add(listener);
  return () => {
    authExpiredListeners.delete(listener);
  };
}

export async function probeAuthSession(): Promise<boolean> {
  if (authProbeInFlight) {
    return authProbeInFlight;
  }
  authProbeInFlight = (async () => {
    try {
      const res = await fetch(apiUrl("/auth/me"), {
        credentials: "include",
      });
      if (res.status === 401) {
        notifyAuthExpired();
        return false;
      }
      return true;
    } catch {
      return true;
    } finally {
      authProbeInFlight = null;
    }
  })();
  return authProbeInFlight;
}

async function request<T>(path: string, init?: RequestOptions): Promise<T> {
  const res = await fetch(apiUrl(path), {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...init?.headers,
    },
  });
  const body = await parseResponseBody(res).catch(() => undefined);
  if (!res.ok) {
    if (res.status === 401 && !init?.suppressAuthExpired) {
      notifyAuthExpired();
    }
    const errorBody =
      body && typeof body === "object" ? body : { error: res.statusText };
    throw Object.assign(new Error((errorBody as { error?: string }).error ?? res.statusText), {
      status: res.status,
      body: errorBody,
    });
  }
  return body as T;
}

async function requestForm<T>(path: string, form: FormData, init?: RequestOptions): Promise<T> {
  const res = await fetch(apiUrl(path), {
    credentials: "include",
    ...init,
    method: init?.method ?? "POST",
    body: form,
    headers: {
      ...init?.headers,
    },
  });
  const body = await parseResponseBody(res).catch(() => undefined);
  if (!res.ok) {
    if (res.status === 401 && !init?.suppressAuthExpired) {
      notifyAuthExpired();
    }
    const errorBody =
      body && typeof body === "object" ? body : { error: res.statusText };
    throw Object.assign(new Error((errorBody as { message?: string; error?: string }).message ?? (errorBody as { error?: string }).error ?? res.statusText), {
      status: res.status,
      body: errorBody,
    });
  }
  return body as T;
}

// -- Auth --

export interface User {
  user_id: string;
  telegram_language?: string | null;
  email?: string;
  control_plane_version?: string | null;
  onboarding_survey_completed?: boolean;
  providers?: string[];
  telegram?: {
    status: "linked" | "connected";
    username?: string | null;
    display_name?: string | null;
    photo_url?: string | null;
    bot_reachable: boolean;
  } | null;
}

export interface AuthSession {
  id: string;
  current: boolean;
  client_type: "web" | "cli" | "mobile" | "telegram" | "unknown";
  client_name?: string | null;
  device_label?: string | null;
  browser_name?: string | null;
  os_name?: string | null;
  location_label: string;
  created_at: number;
  last_active_at: number;
  expires_at: number;
  revoked_at?: number | null;
}

export function getMe(opts?: { suppressAuthExpired?: boolean; telegramInitData?: string; vpsId?: string }) {
  const query = opts?.vpsId ? `?vps_id=${encodeURIComponent(opts.vpsId)}` : "";
  return request<User>(`/auth/me${query}`, {
    suppressAuthExpired: opts?.suppressAuthExpired,
    ...(opts?.telegramInitData ? { headers: { "X-Telegram-Init-Data": opts.telegramInitData } } : {}),
  });
}

export function startEmailLogin(email: string, turnstileToken?: string, requestLocale?: Locale) {
  return request<{ ok: boolean }>("/auth/email/start", {
    method: "POST",
    body: JSON.stringify({ email, ...(turnstileToken ? { turnstile_token: turnstileToken } : {}), ...(requestLocale ? { request_locale: requestLocale } : {}) }),
  });
}

export function logout() {
  return request<{ ok: boolean }>("/auth/logout", { method: "POST" });
}

export function listAuthSessions() {
  return request<{ sessions: AuthSession[] }>("/auth/sessions");
}

export function revokeAuthSession(id: string) {
  return request<{ ok: boolean; revoked_session_id: string; current_revoked: boolean }>(
    `/auth/sessions/${encodeURIComponent(id)}/revoke`,
    { method: "POST" },
  );
}

export function revokeOtherAuthSessions() {
  return request<{ ok: boolean; revoked_count: number }>("/auth/sessions/revoke-others", {
    method: "POST",
  });
}

export function unlinkProvider(provider: "google" | "github" | "telegram") {
  return request<{ ok: boolean; providers: string[] }>(`/auth/${provider}/disconnect`, {
    method: "POST",
  });
}

export function getUserSettings() {
  return request<{ preferences: UserPreferences }>("/me/settings");
}

export interface FirstTouchPayload {
  anonymous_id: string;
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  term?: string | null;
  content?: string | null;
  landing_url?: string | null;
  referrer?: string | null;
  telegram_start_param?: string | null;
  partner_key?: string | null;
  consent?: { ad_storage?: boolean };
  ad_ids?: Partial<Record<"gclid" | "gbraid" | "wbraid" | "msclkid" | "fbclid", string>>;
}

export function recordFirstTouch(payload: FirstTouchPayload) {
  return request<{ ok: boolean }>("/analytics/first-touch", {
    method: "POST",
    body: JSON.stringify(payload),
    suppressAuthExpired: true,
  });
}

export type ProductAnalyticsEvent =
  | "server_created"
  | "server_deleted"
  | "session_created"
  | "session_deleted"
  | "session_tab_auto_closed"
  | "terminal_input_sent"
  | "file_uploaded"
  | "voice_transcription_completed";

export type ProductAnalyticsSource =
  | "web_app"
  | "telegram_mini_app"
  | "telegram_bot"
  | "api"
  | "unknown";

export function trackProductAnalyticsEvent(
  event: ProductAnalyticsEvent,
  properties: Record<string, unknown> = {},
) {
  void request<void>("/me/analytics/events", {
    method: "POST",
    body: JSON.stringify({ event, properties }),
    suppressAuthExpired: true,
  }).catch(() => {});
}

function currentProductAnalyticsSource(): ProductAnalyticsSource {
  if (typeof window === "undefined") return "unknown";
  if (window.location.pathname.startsWith("/tg/")) return "telegram_mini_app";
  return "web_app";
}

function byteLengthBucket(byteLength: number): string {
  if (byteLength <= 4) return "1-4";
  if (byteLength <= 32) return "5-32";
  if (byteLength <= 128) return "33-128";
  if (byteLength <= 1024) return "129-1024";
  return "1025+";
}

export function attachAcquisition(anonymousId: string | null) {
  return request<{ ok: boolean; attached: boolean }>("/me/acquisition/attach", {
    method: "POST",
    body: JSON.stringify({ anonymous_id: anonymousId }),
  });
}

export interface OnboardingSurveyQuestion {
  id: string;
  title: string;
  options: string[];
}

export interface OnboardingSurvey {
  schema_version: number;
  status: "pending" | "completed";
  country: string | null;
  completed_at: number | null;
  answers: Record<string, string>;
  questions: OnboardingSurveyQuestion[];
}

export function getOnboardingSurvey() {
  return request<OnboardingSurvey>("/me/onboarding-survey");
}

export function completeOnboardingSurvey(input: {
  answers: Record<string, string>;
  country?: string | null;
}) {
  return request<{ ok: boolean; schema_version: number; status: "completed" }>(
    "/me/onboarding-survey",
    {
      method: "PUT",
      body: JSON.stringify(input),
    },
  );
}

export function updateUserSettings(opts: Partial<UserPreferences>) {
  return request<{ preferences: UserPreferences }>("/me/settings", {
    method: "PATCH",
    body: JSON.stringify(opts),
  });
}

export type AgentUsageLeaderboardPeriod = "day" | "week" | "month" | "year";
export type AgentUsageLeaderboardMetric = "total" | "codex" | "claude";

export interface AgentUsageLeaderboard {
  period: AgentUsageLeaderboardPeriod;
  metric: AgentUsageLeaderboardMetric;
  generated_at: number;
  channel_gate?: {
    channel_url: string;
    message: string;
  } | null;
  participant_count: number;
  totals: {
    total_tokens: number;
    codex_tokens: number;
    claude_tokens: number;
    days_since_first_usage: number;
  };
  viewer: {
    rank: number | null;
    total_tokens: number;
    codex_tokens: number;
    claude_tokens: number;
    streak_days: number;
    in_top: boolean;
  } | null;
  leaders: Array<{
    rank: number;
    display_name: string;
    total_tokens: number;
    codex_tokens: number;
    claude_tokens: number;
  }>;
}

export function getAgentUsageLeaderboard(input: {
  period?: AgentUsageLeaderboardPeriod;
  metric?: AgentUsageLeaderboardMetric;
  limit?: number;
  surface?: "top";
  telegramInitData: string;
}) {
  const params = new URLSearchParams();
  if (input.period) params.set("period", input.period);
  if (input.metric) params.set("metric", input.metric);
  if (input.limit) params.set("limit", String(input.limit));
  if (input.surface) params.set("surface", input.surface);
  const query = params.toString();
  return request<AgentUsageLeaderboard>(`/agent-usage/leaderboard${query ? `?${query}` : ""}`, {
    headers: {
      "X-Telegram-Init-Data": input.telegramInitData,
    },
    suppressAuthExpired: true,
  });
}

export interface TelegramFilePreview {
  ok: true;
  relative_path: string;
  path: string;
  filename: string;
  size: number;
  content_type: string;
  line: number | null;
  column: number | null;
  data_base64: string;
  server_name: string;
  workdir: string;
  download_url: string;
}

export function getTelegramFilePreview(token: string) {
  return request<TelegramFilePreview>(`/telegram/file-preview/${encodeURIComponent(token)}`);
}

export function pasteTelegramFilePreviewReference(token: string) {
  return request<{ ok: true }>(`/telegram/file-preview/${encodeURIComponent(token)}`, {
    method: "POST",
  });
}

export function createTelegramFilePreviewLinkToken(
  token: string,
  input: { label: string; href: string },
) {
  return request<{ ok: true; token: string }>(`/telegram/file-preview/${encodeURIComponent(token)}/link`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// -- VPS --

export interface VPS {
  id: string;
  user_id: string;
  provider?: "digitalocean" | "manual";
  label?: string;
  droplet_id?: number;
  region?: string;
  size?: string;
  ipv4?: string | null;
  status: string;
  created_at: number;
  updated_at?: number;
  gateway_id?: string;
  gateway_connected?: boolean;
  gateway_version?: string | null;
  gateway_os?: string | null;
  gateway_metadata?: Record<string, unknown> | null;
  default_gateway_version?: string;
  control_plane_version?: string | null;
  active_session_count?: number;
  telegram_managed_bot?: {
    username: string | null;
    name: string | null;
    status: string;
    connected: boolean;
  } | null;
  telegram_manager_topic?: {
    topic_id: string;
    topic_name: string;
  } | null;
}

export interface TelegramOrphanedManagedBot {
  id: string;
  source: "detached" | "unmatched";
  username: string | null;
  name: string | null;
  status: "detached" | "unmatched";
}

export function listVPS() {
  return request<{ vps: VPS[]; telegram_orphaned_managed_bots?: TelegramOrphanedManagedBot[] }>("/vps");
}

export function getVPS(id: string) {
  return request<VPS>(`/vps/${encodeURIComponent(id)}`);
}

export function listWorkspaceFolders(vpsId: string) {
  return request<{ folders: string[] }>(`/vps/${encodeURIComponent(vpsId)}/workspace-folders`);
}

export function updateVPS(id: string, opts: { label: string }) {
  return request<VPS>(`/vps/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(opts),
  });
}

export interface CreateVPSResponse {
  status: "provisioning";
  vps: VPS;
}

export interface DODropletRegionOption {
  slug: string;
  city: string;
  label: string;
  available: boolean;
}

export interface DODropletRegionColumn {
  id: "americas" | "europe" | "asia_pacific";
  label: string;
  options: DODropletRegionOption[];
}

export interface DODropletSizeOption {
  slug: string;
  label: string;
  specs: string;
  vcpus: number;
  memory: number;
  price_monthly: number;
  regions: string[];
}

export interface DODropletImageOption {
  slug: string;
  family: "ubuntu" | "debian";
  label: string;
}

export interface DODropletOptions {
  live: boolean;
  regions: DODropletRegionColumn[];
  plans: {
    regular: DODropletSizeOption[];
    premium_intel: DODropletSizeOption[];
    premium_amd: DODropletSizeOption[];
  };
  images: DODropletImageOption[];
  defaults: {
    region: string;
    plan_family: "regular" | "premium_intel" | "premium_amd";
    size: string;
    image: string;
  };
}

export function getDODropletOptions() {
  return request<DODropletOptions>("/vps/options");
}

export function createVPS(opts: {
  region?: string;
  size?: string;
  image?: string;
  label?: string;
}) {
  return request<CreateVPSResponse>("/vps", {
    method: "POST",
    body: JSON.stringify(opts),
  }).then((response) => {
    trackProductAnalyticsEvent("server_created", {
      vps_id: response.vps.id,
      server_kind: "digitalocean",
      droplet_region: opts.region ?? response.vps.region,
      droplet_size: opts.size ?? response.vps.size,
      droplet_image: opts.image ?? "unknown",
      source: currentProductAnalyticsSource(),
    });
    return response;
  });
}

export interface ManualVPSResponse {
  gateway_id: string;
  gateway_auth_token: string;
  cp_url: string;
  install: {
    linux: string;
    macos: string;
  };
  vps: VPS;
}

export function createManualVPS(opts?: { label?: string }) {
  return request<ManualVPSResponse>("/vps/manual", {
    method: "POST",
    body: JSON.stringify(opts ?? {}),
  }).then((response) => {
    trackProductAnalyticsEvent("server_created", {
      vps_id: response.vps.id,
      server_kind: "manual",
      source: currentProductAnalyticsSource(),
    });
    return response;
  });
}

export function regenerateManualVPSCommand(id: string) {
  return request<ManualVPSResponse>(`/vps/${encodeURIComponent(id)}/manual-command`, {
    method: "POST",
  });
}

export function deleteVPS(id: string) {
  return request<void>(`/vps/${encodeURIComponent(id)}`, {
    method: "DELETE",
  }).then((response) => {
    trackProductAnalyticsEvent("server_deleted", {
      vps_id: id,
      source: currentProductAnalyticsSource(),
    });
    return response;
  });
}

export function powerOffVPS(id: string) {
  return request<{ ok: boolean }>(
    `/vps/${encodeURIComponent(id)}/power-off`,
    { method: "POST" },
  );
}

export function powerOnVPS(id: string) {
  return request<{ ok: boolean }>(
    `/vps/${encodeURIComponent(id)}/power-on`,
    { method: "POST" },
  );
}

export function requestGatewayUpdate(id: string) {
  return request<{ ok: boolean; status: string; request_id: string; gateway_id: string; target_version: string }>(
    `/vps/${encodeURIComponent(id)}/gateway-update`,
    { method: "POST" },
  );
}

// -- Sessions --

export interface Session {
  id: string;
  user_id: string;
  vps_id: string;
  title: string;
  agent_type: string;
  workdir: string;
  status: string;
  created_at: number;
  last_activity_at?: number;
}

export interface CreateSessionOptions {
  title?: string;
  agent_type?: string;
  workdir?: string;
  sandbox_mode?: boolean;
}

export interface ListSessionsOptions {
  page?: number;
  pageSize?: number;
  status?: "all" | "open";
  reconcile?: boolean;
}

export interface ListSessionsResponse {
  sessions: Session[];
  page: number;
  page_size: number;
  total: number;
  open_total: number;
  has_more: boolean;
}

export function listSessions(vpsId: string, opts: ListSessionsOptions = {}) {
  const params = new URLSearchParams();
  if (opts.page) params.set("page", String(opts.page));
  if (opts.pageSize) params.set("page_size", String(opts.pageSize));
  if (opts.status && opts.status !== "all") params.set("status", opts.status);
  if (opts.reconcile) params.set("reconcile", "1");
  const query = params.toString();
  return request<ListSessionsResponse>(
    `/vps/${encodeURIComponent(vpsId)}/sessions${query ? `?${query}` : ""}`,
  );
}

export function createSession(
  vpsId: string,
  opts: CreateSessionOptions,
) {
  return request<{ session_id: string; title: string; status: string }>(
    `/vps/${encodeURIComponent(vpsId)}/sessions`,
    { method: "POST", body: JSON.stringify(opts) },
  ).then((response) => {
    trackProductAnalyticsEvent("session_created", {
      vps_id: vpsId,
      session_id: response.session_id,
      agent_type: opts.agent_type ?? "unknown",
      sandbox_mode: opts.sandbox_mode === true ? "on" : opts.sandbox_mode === false ? "off" : "unknown",
      source: currentProductAnalyticsSource(),
    });
    return response;
  });
}

export function getSession(vpsId: string, sessionId: string) {
  return request<Session>(
    `/vps/${encodeURIComponent(vpsId)}/sessions/${encodeURIComponent(sessionId)}`,
  );
}

export function deleteSession(vpsId: string, sessionId: string) {
  return request<void>(
    `/vps/${encodeURIComponent(vpsId)}/sessions/${encodeURIComponent(sessionId)}`,
    { method: "DELETE" },
  ).then((response) => {
    trackProductAnalyticsEvent("session_deleted", {
      vps_id: vpsId,
      session_id: sessionId,
      source: currentProductAnalyticsSource(),
    });
    return response;
  });
}

export function updateSession(vpsId: string, sessionId: string, opts: { title: string }) {
  return request<Session>(
    `/vps/${encodeURIComponent(vpsId)}/sessions/${encodeURIComponent(sessionId)}`,
    { method: "PATCH", body: JSON.stringify(opts) },
  );
}

export interface PlanLimitError {
  code: "server_limit_reached" | "session_limit_reached";
  error: string;
  limit: number;
  current_count?: number;
  plan_id?: PlanId;
  plan_name?: string;
  upgrade_plan_id?: Exclude<PlanId, "free"> | null;
  upgrade_plan_name?: string | null;
}

export function getPlanLimitError(err: unknown): PlanLimitError | null {
  const status = typeof err === "object" && err !== null ? (err as { status?: unknown }).status : null;
  const body = typeof err === "object" && err !== null ? (err as { body?: unknown }).body : null;
  if (status !== 409 || !body || typeof body !== "object") return null;
  const candidate = body as Partial<PlanLimitError>;
  if (candidate.code !== "server_limit_reached" && candidate.code !== "session_limit_reached") return null;
  if (typeof candidate.limit !== "number" || !Number.isFinite(candidate.limit)) return null;
  return {
    code: candidate.code,
    error: typeof candidate.error === "string" ? candidate.error : `${candidate.code} (${candidate.limit})`,
    limit: candidate.limit,
    current_count: typeof candidate.current_count === "number" ? candidate.current_count : undefined,
    plan_id: candidate.plan_id,
    plan_name: candidate.plan_name,
    upgrade_plan_id: candidate.upgrade_plan_id ?? null,
    upgrade_plan_name: candidate.upgrade_plan_name ?? null,
  };
}

export function formatPlanLimitTitle(limit: PlanLimitError): string {
  return t(limit.code === "server_limit_reached" ? "web:plan_server_limit" : "web:plan_session_limit");
}

export function formatPlanLimitDescription(limit: PlanLimitError): string {
  const allowed = t(limit.code === "server_limit_reached" ? "web:plan_servers" : "web:plan_sessions", { count: limit.limit });
  const planName = limit.plan_name || t("web:plan_current");
  const count = typeof limit.current_count === "number" ? limit.current_count : limit.limit;
  if (limit.code === "server_limit_reached" && count > limit.limit) {
    const resolution = limit.upgrade_plan_name
      ? t("web:plan_limit_remove_upgrade", { plan: limit.upgrade_plan_name })
      : t("web:plan_limit_remove");
    return `${t("web:plan_limit", { plan: planName, allowed, current: count })} ${resolution}`;
  }
  const upgrade = limit.upgrade_plan_name ? t("web:plan_limit_upgrade", { plan: limit.upgrade_plan_name }) : t("web:plan_limit_upgrade_generic");
  return `${t("web:plan_limit", { plan: planName, allowed, current: count })} ${upgrade}`;
}

export type VoiceTranscriptionAllowance =
  | { allowed: true }
  | { allowed: false; reason: string; userMessage: string };

export interface VoiceTranscriptionResult {
  ok: true;
  text: string;
  provider: "groq";
  model: string;
  elapsed_ms: number;
}

export function getVoiceTranscriptionAllowance() {
  return request<VoiceTranscriptionAllowance>("/me/features/voice-transcription");
}

export type PlanId = "free" | "starter" | "plus" | "pro";
export type BillingProvider = "none" | "stripe" | "telegram_stars";
export type BillingInterval = "none" | "monthly" | "yearly";
export type SubscriptionStatus = "none" | "active" | "past_due" | "canceled" | "trialing";

export interface PlanEntitlement {
  plan_id: PlanId;
  plan_name: string;
  billing_provider: BillingProvider;
  billing_interval: BillingInterval;
  subscription_status: SubscriptionStatus;
  server_limit: number;
  active_session_limit_total: number;
  unlimited_voice_transcriptions: boolean;
  priority_support: boolean;
  cancel_at_period_end: boolean;
  current_period_end: number | null;
  stripe_custom_amount_usd: number | null;
  feature_flags: string[];
}

export interface PlanUsage {
  server_count: number;
  active_session_count: number;
}

export function getPlanLimitFromUsage(
  kind: "server" | "session",
  entitlement: PlanEntitlement,
  usage: PlanUsage,
): PlanLimitError | null {
  if (kind === "session" && usage.server_count > entitlement.server_limit) {
    const upgradePlan = nextPlanForLimit("server", entitlement, usage.server_count);
    return {
      code: "server_limit_reached",
      error: `server limit reached (${entitlement.server_limit})`,
      limit: entitlement.server_limit,
      current_count: usage.server_count,
      plan_id: entitlement.plan_id,
      plan_name: entitlement.plan_name,
      upgrade_plan_id: upgradePlan?.id ?? null,
      upgrade_plan_name: upgradePlan?.name ?? null,
    };
  }

  const limit = kind === "server" ? entitlement.server_limit : entitlement.active_session_limit_total;
  const currentCount = kind === "server" ? usage.server_count : usage.active_session_count;
  if (currentCount < limit) return null;
  const requiredCount = currentCount + 1;
  const upgradePlan = nextPlanForLimit(kind, entitlement, requiredCount);
  return {
    code: kind === "server" ? "server_limit_reached" : "session_limit_reached",
    error: `${kind} limit reached (${limit})`,
    limit,
    current_count: currentCount,
    plan_id: entitlement.plan_id,
    plan_name: entitlement.plan_name,
    upgrade_plan_id: upgradePlan?.id ?? null,
    upgrade_plan_name: upgradePlan?.name ?? null,
  };
}

function nextPlanForLimit(
  kind: "server" | "session",
  entitlement: PlanEntitlement,
  requiredCount: number,
): { id: Exclude<PlanId, "free">; name: string } | null {
  const plans: Array<{ id: Exclude<PlanId, "free">; name: string; serverLimit: number; sessionLimit: number }> = [
    { id: "starter", name: "Starter", serverLimit: 1, sessionLimit: 5 },
    { id: "plus", name: "Plus", serverLimit: 2, sessionLimit: 20 },
    { id: "pro", name: "Pro", serverLimit: 5, sessionLimit: 100 },
  ];
  const currentRank = planRank(entitlement.plan_id);
  return plans.find((plan) => {
    if (planRank(plan.id) <= currentRank) return false;
    return kind === "server"
      ? plan.serverLimit >= requiredCount
      : plan.sessionLimit >= requiredCount;
  }) ?? null;
}

function planRank(planId: PlanId): number {
  switch (planId) {
    case "starter":
      return 1;
    case "plus":
      return 2;
    case "pro":
      return 3;
    default:
      return 0;
  }
}

export interface BillingPlanOption {
  id: PlanId;
  name: string;
  server_limit: number;
  active_session_limit_total: number;
  unlimited_voice_transcriptions: boolean;
  priority_support: boolean;
  most_popular: boolean;
  stripe: {
    monthly_usd: number;
    yearly_usd: number;
    available: boolean;
    name_your_price: {
      monthly_min_usd: number;
      monthly_default_usd: number;
      monthly_max_usd: number;
      yearly_min_usd: number;
      yearly_default_usd: number;
      yearly_max_usd: number;
    } | null;
  } | null;
  telegram_stars: { monthly_stars: number; available: boolean } | null;
}

export interface PremiumServiceOption {
  id: "smbuddy_premium";
  name: string;
  monthly_usd: number;
  external_url: string;
  description: string;
}

export interface BillingOptions {
  entitlement: PlanEntitlement;
  usage: PlanUsage;
  plans: BillingPlanOption[];
  premium_service: PremiumServiceOption;
  hosted_commerce_enabled: boolean;
  scheduled_change: BillingScheduledChange | null;
}

export interface BillingScheduledChange {
  plan_id: Exclude<PlanId, "free">;
  plan_name: string;
  billing_interval: Exclude<BillingInterval, "none">;
  effective_at: number;
  custom_amount_usd: number | null;
}

export function getPlan() {
  return request<{ entitlement: PlanEntitlement; usage: PlanUsage }>("/me/plan");
}

export function getBillingOptions() {
  return request<BillingOptions>("/me/billing/options");
}

export function createStripeCheckoutSession(input: {
  planId: Exclude<PlanId, "free">;
  interval: Exclude<BillingInterval, "none">;
  customAmountUsd?: number;
}) {
  return request<{ url: string }>("/me/billing/stripe/checkout", {
    method: "POST",
    body: JSON.stringify({
      plan_id: input.planId,
      interval: input.interval,
      ...(input.customAmountUsd === undefined ? {} : { custom_amount_usd: input.customAmountUsd }),
    }),
  });
}

export function changeStripeSubscription(input: {
  planId: Exclude<PlanId, "free">;
  interval: Exclude<BillingInterval, "none">;
  customAmountUsd?: number;
}) {
  return request<{ ok: true; unchanged?: true; scheduled?: boolean; current_period_end?: number | null }>(
    "/me/billing/stripe/subscription/change",
    {
      method: "POST",
      body: JSON.stringify({
        plan_id: input.planId,
        interval: input.interval,
        ...(input.customAmountUsd === undefined ? {} : { custom_amount_usd: input.customAmountUsd }),
      }),
    },
  );
}

export function cancelStripeSubscriptionChange() {
  return request<{ ok: true }>("/me/billing/stripe/subscription/change/cancel", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function updateSubscriptionAutoRenew(input: { isCanceled: boolean }) {
  return request<{ ok: true }>("/me/billing/subscription/auto-renew", {
    method: "POST",
    body: JSON.stringify({ is_canceled: input.isCanceled }),
  });
}

export function createTelegramStarterInvoiceLink() {
  return request<{ url: string }>("/me/billing/telegram/starter-invoice", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function createStripePortalSession() {
  return request<{ url: string }>("/me/billing/stripe/portal", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function transcribeSessionVoice(
  vpsId: string,
  sessionId: string,
  input: {
    blob: Blob;
    fileName: string;
    durationMs: number;
    mimeType?: string | null;
    signal?: AbortSignal;
  },
) {
  const form = new FormData();
  form.set("duration_ms", String(Math.max(0, Math.round(input.durationMs))));
  if (input.mimeType) form.set("mime_type", input.mimeType);
  form.set("file", input.blob, input.fileName);
  return requestForm<VoiceTranscriptionResult>(
    `/vps/${encodeURIComponent(vpsId)}/sessions/${encodeURIComponent(sessionId)}/transcriptions`,
    form,
    { signal: input.signal },
  ).then((response) => {
    trackProductAnalyticsEvent("voice_transcription_completed", {
      vps_id: vpsId,
      session_id: sessionId,
      provider: response.provider,
      model: response.model,
      elapsed_ms_bucket: elapsedMsBucket(response.elapsed_ms),
      duration_ms_bucket: elapsedMsBucket(input.durationMs),
      audio_byte_length_bucket: byteLengthBucket(input.blob.size),
      mime_family: mimeFamily(input.mimeType ?? input.blob.type),
      source: currentProductAnalyticsSource(),
    });
    return response;
  });
}

function elapsedMsBucket(ms: number): string {
  if (ms <= 1_000) return "0-1s";
  if (ms <= 3_000) return "1-3s";
  if (ms <= 10_000) return "3-10s";
  if (ms <= 30_000) return "10-30s";
  if (ms <= 60_000) return "30-60s";
  return "60s+";
}

function mimeFamily(mimeType: string | null | undefined): string {
  const value = (mimeType ?? "").toLowerCase();
  if (value.includes("webm")) return "webm";
  if (value.includes("mp4") || value.includes("m4a")) return "mp4";
  if (value.includes("ogg")) return "ogg";
  if (value.startsWith("audio/")) return "audio_other";
  return "unknown";
}

// -- Agents --

export interface AgentStatusEntry {
  agent: string;
  binary: string;
  installed: boolean;
  version?: string;
  package_name?: string;
  latest_version?: string;
  update_available?: boolean;
}

export type ManagedAgentType = "claude-code" | "codex" | "gemini" | "antigravity" | "kimi" | "pi" | "opencode";

export interface AgentListResponse {
  agents: AgentStatusEntry[];
  installations?: AgentInstallationStatus[];
}

export function listAgents(vpsId: string) {
  return request<AgentListResponse>(
    `/vps/${encodeURIComponent(vpsId)}/agents`,
  );
}

export function installAgent(vpsId: string, agent: ManagedAgentType) {
  return request<{ ok: boolean; status: string; request_id: string; agent: ManagedAgentType }>(
    `/vps/${encodeURIComponent(vpsId)}/agents/install`,
    {
      method: "POST",
      body: JSON.stringify({ agent }),
    },
  );
}

export function isManagedAgentType(value: string): value is ManagedAgentType {
  return value === "claude-code" || value === "codex" || value === "gemini" || value === "antigravity" || value === "kimi" || value === "pi" || value === "opencode";
}

export function getMissingAgentFromError(err: unknown): ManagedAgentType | null {
  if (!(err instanceof Error)) return null;
  const body = (err as Error & { body?: unknown }).body;
  if (body && typeof body === "object") {
    const code = (body as { code?: unknown }).code;
    const agent = (body as { agent?: unknown }).agent;
    if (code === "agent_not_installed" && typeof agent === "string" && isManagedAgentType(agent)) {
      return agent;
    }
  }

  const match = err.message.match(/^([a-z-]+) is not installed\. Run agents\.install first\.$/);
  if (!match) return null;
  return isManagedAgentType(match[1]) ? match[1] : null;
}

// Match the attempt that this UI started, not another tab's older failure.
export class AgentInstallError extends Error {
  name = "AgentInstallError";
}

export function agentInstallProgress(response: AgentListResponse, requestId?: string) {
  const result = requestId
    ? response.installations?.find((entry) => entry.request_id === requestId)
    : undefined;
  if (requestId && response.installations && !result) {
    throw new AgentInstallError("Installation status is no longer available. The gateway may have restarted; check installed agents and retry.");
  }
  if (result?.status === "failed") {
    throw new AgentInstallError(result.error || "Installation failed. Check the gateway logs for details.");
  }
  return result?.status;
}

export async function waitForAgentInstalled(
  vpsId: string,
  agent: ManagedAgentType,
  opts?: { timeoutMs?: number; intervalMs?: number; requestId?: string },
) {
  const timeoutMs = opts?.timeoutMs ?? 90_000;
  const intervalMs = opts?.intervalMs ?? 2_000;
  let deadline = Date.now() + timeoutMs;
  let running = false;

  while (running || Date.now() < deadline) {
    const response = await listAgents(vpsId);
    const progress = agentInstallProgress(response, opts?.requestId);
    // New gateways explicitly report queued/active jobs. The timeout limits
    // missing status (including legacy gateways), not a healthy slow install.
    running = progress === "running";
    if (running) deadline = Date.now() + timeoutMs;
    const target = response.agents.find((entry) => entry.agent === agent);
    if (progress !== "running" && target?.installed) return target;
    await new Promise((resolve) => window.setTimeout(resolve, intervalMs));
  }

  throw new Error(`${agent} installation timed out. Try again in a moment.`);
}

export async function installAgentAndCreateSession(
  vpsId: string,
  agent: ManagedAgentType,
  opts: CreateSessionOptions,
) {
  const { request_id } = await installAgent(vpsId, agent);
  await waitForAgentInstalled(vpsId, agent, { requestId: request_id });
  return createSession(vpsId, opts);
}

// -- DO Connection --

export function getOAuthURL(
  provider: "google" | "github" | "telegram",
  redirectUrl?: string,
  turnstileToken?: string,
  requestLocale?: Locale,
) {
  const url = new URL(apiUrl(`/auth/${provider}/start`), currentOrigin());
  if (redirectUrl) {
    url.searchParams.set("redirect_url", redirectUrl);
  }
  if (turnstileToken) {
    url.searchParams.set("turnstile_token", turnstileToken);
  }
  if (requestLocale) url.searchParams.set("request_locale", requestLocale);
  return url.toString();
}

function currentOrigin(): string {
  const loc = globalThis.location;
  if (loc.origin) return loc.origin;
  if (loc.protocol && loc.host) return `${loc.protocol}//${loc.host}`;
  return "http://localhost";
}

export function getOAuthLinkStartURL(
  provider: "google" | "github",
  redirectUrl?: string,
) {
  const url = new URL(apiUrl(`/auth/${provider}/link/start`));
  if (redirectUrl) {
    url.searchParams.set("redirect_url", redirectUrl);
  }
  return url.toString();
}

export function getDOLinkStartURL(redirectUrl?: string) {
  const url = new URL(getAccountLinkURL("do"));
  if (redirectUrl) {
    url.searchParams.set("redirect_url", redirectUrl);
  }
  return url.toString();
}

export function getAccountLinkURL(provider: "do" | "telegram") {
  return apiUrl(provider === "do" ? "/auth/do" : "/auth/telegram/link/start");
}

export function getTelegramLinkStartURL(redirectUrl?: string) {
  const url = new URL(getAccountLinkURL("telegram"));
  if (redirectUrl) {
    url.searchParams.set("redirect_url", redirectUrl);
  }
  return url.toString();
}

export function disconnectDO() {
  return request<{ ok: boolean; providers?: string[] }>("/auth/do/disconnect", { method: "POST" });
}
